import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../../api/client";

function hasManageRights(user) {
  return Array.isArray(user?.AccessRights) &&
    user.AccessRights.includes("net.thomasdye.TDS-serverstatus.CoreServers.Manage");
}

export default function InfraHostDetailsPage({ user }) {
  const { systemId } = useParams();
  const [hostData, setHostData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        setLoading(true);
        // PHP: proxy collector /admin/hosts/:id -> returns full snapshot
        // { systemId, host, takenAt, services: [...] }
        const { data } = await api.get(`/collector/hosts/${encodeURIComponent(systemId)}`);
        if (ok) setHostData(data);
      } catch (e) {
        console.error(e);
        if (ok) setHostData(null);
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => { ok = false; };
  }, [systemId]);

  async function doRefresh() {
    setRefreshing(true);
    try {
      await api.post(`/collector/hosts/${encodeURIComponent(systemId)}/refresh`);
      const { data } = await api.get(`/collector/hosts/${encodeURIComponent(systemId)}`);
      setHostData(data);
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshing(false);
    }
  }

  const services = useMemo(() => Array.isArray(hostData?.services) ? hostData.services : [], [hostData]);

  return (
    <div style={{ padding: 24 }}>
      {loading ? (
        <p>Loading…</p>
      ) : !hostData ? (
        <p>Host not found.</p>
      ) : (
        <>
          {/* Title for the host */}
          <h1 style={{ marginBottom: 4 }}>
            {hostData.host || "(unknown host)"}
          </h1>
          <div style={{ color: "#666", marginBottom: 16 }}>
            <code>{hostData.systemId}</code>
            {" · "}
            Last snapshot: {hostData.takenAt ? new Date(hostData.takenAt).toLocaleString() : "—"}
            {" · "}
            Services: {services.length}
          </div>

          {/* Manage buttons */}
          <div style={{ marginBottom: 20 }}>
            <Link to="/infra/hosts">← Back to hosts</Link>
            {" "}
            {hasManageRights(user) && (
              <button
                onClick={doRefresh}
                disabled={refreshing}
                style={{ marginLeft: 12, padding: "6px 10px" }}
              >
                {refreshing ? "Refreshing…" : "Refresh snapshot"}
              </button>
            )}
          </div>

          {/* Services list (clickable) */}
          {services.length === 0 ? (
            <p>No services reported.</p>
          ) : (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
              {services.map((s) => {
                const gid = encodeURIComponent(s.gid || `${hostData.systemId}:${s.service}`);
                const updated = s.updatedAt ? new Date(s.updatedAt).toLocaleString() : "—";
                return (
                  <Link
                    key={s.gid || s.id}
                    to={`/infra/hosts/${encodeURIComponent(hostData.systemId)}/services/${gid}`}
                    style={{
                      display:"block", padding:12, border:"1px solid #ddd",
                      borderRadius:8, textDecoration:"none", color:"inherit"
                    }}
                  >
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                      <strong>{s.service}</strong>
                      <span
                        style={{
                          fontSize:12, padding:"2px 6px",
                          borderRadius:999,
                          background: s.healthy ? "#e6ffed" : "#ffecec",
                          border: `1px solid ${s.healthy ? "#84d694" : "#ff9c9c"}`
                        }}
                      >
                        {s.healthy ? "Healthy" : "Issue"}
                      </span>
                    </div>
                    <div style={{ fontSize:13, color:"#555" }}>
                      Platform: {s.platform} · Active: {s.active}
                    </div>
                    {s.description ? (
                      <div style={{ marginTop:6, fontSize:13 }}>{s.description}</div>
                    ) : null}
                    <div style={{ marginTop:8, fontSize:12, color:"#777" }}>
                      Updated: {updated}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
